<script lang="ts" setup>
import { mdiArrowLeft, mdiArrowRight } from '@mdi/js'

import TextButton from '@/components/business/TextButton.vue'
import PlanSelection from '@/components/business/upgrade/PlanSelection.vue'
import PlanUpgradeCard from '@/components/business/upgrade/PlanUpgradeCard.vue'
import BillingForm from '@/components/forms/BillingForm.vue'
import ToolbarBase from '@/components/ToolbarBase.vue'
import { isAbortError } from '@/utils/error'
import { PaymentError } from '@/utils/payment'

type View = 'plans' | 'upgrade'

definePageMeta({
  layout: false,
  accessControl: {
    roles: ['user', 'admin'],
    isStrict: true,
  },
})

const { paymentRepository } = useApi()
const { $adcell } = useNuxtApp()

const businessStore = useBusinessStore()
const paymentStore = usePaymentStore()
const userStore = useUserStore()
const systemStore = useSystemStore()
const widgetStore = useWidgetStore()
const { getCurrentPaymentMethod } = usePaymentStore()

const { t } = useI18n()
const { logError } = useErrorHandling()

const route = useRoute()

const platform = computed(() => systemStore.platform)

const icons = reactive({
  arrowLeft: mdiArrowLeft,
  arrowRight: mdiArrowRight,
})

const isLoading = ref(false)
const isFetchingPrice = ref(false)
const paymentMethod = ref<CardDetails | null>(null)

const businessName = computed(() => {
  if (businessStore.name) return businessStore.name
  else return `${userStore.firstName} ${userStore.lastName}`
})

const isFreeUser = computed(() => !businessStore.id)

const hasPaymentCardOrBankAccount = computed(
  () => Boolean(paymentMethod.value?.card) || Boolean(paymentMethod.value?.bankAccount)
)

const { isLocked, isTrialing, isTrialExpired, isChargebeeTrial } = storeToRefs(useBusinessStore())

const proPlanActive = computed(() => businessStore.currentPricePlan?.type === 'PRO_V1' && !businessStore.isTrialing)

const blockedBusiness = computed(() => isLocked.value || isTrialExpired.value)

/**
 * A plan upgrade skips the billing form since the business already has payment details on file.
 * This excludes trials, free users, blocked businesses, and starter plans.
 */
const isPlanUpgrade = computed(
  () => !isTrialing.value && !isFreeUser.value && !blockedBusiness.value && !businessStore.isStarterPlan
)

const isFirstPaidSubscription = computed(
  () => isFreeUser.value || isTrialing.value || isTrialExpired.value || businessStore.isStarterPlan
)

/* maintain UI consistency after conversion completes but view not changed */
const trialConvert = ref(false)

const viewInFocus = ref<View>('plans')

/**
 * FIXME: We don't know what error messages Chargebee supports
 * we are using payment components these will reach GA (general availability) end of Q1 26
 * https://www.chargebee.com/checkout-portal-docs/payment-components.html
 */
const showError = () => {
  widgetStore.createSnackbar({
    message: t('billing.settings.cc.error.setup'),
  })
  isLoading.value = false
}

const selectionRef = computed(() => selection)
const seats = ref(1)
const selectedCouponCode = ref('')

const {
  paymentData,
  generatePaymentIntent,
  abortPaymentIntent,
  error: paymentError,
} = usePaymentIntent(selectionRef, seats, selectedCouponCode)

watch(paymentError, err => {
  if (err) showError()
})

const toolbarTitle = computed<string>(() => {
  switch (viewInFocus.value) {
    case 'plans':
      return t('business.upgrade.toolbar_title.plans') as string
    case 'upgrade':
      return t('business.upgrade.toolbar_title.upgrade', { name: selection.plan?.name }) as string
    default:
      return ''
  }
})

const pricePlans = ref<PricePlan[]>([])

const updatePricePlans = async () => {
  if (!pricePlans.value.length) {
    pricePlans.value = await paymentStore.getBusinessPricePlans()
  }
}

const getPlan = (planName: Lowercase<PricePlanName>) => {
  return (
    pricePlans.value.find(plan => {
      return plan.name.toLowerCase() === planName
    }) ?? null
  )
}

/**
 * Properties that can be influenced by query parameters. Allows
 * for 'deep-linking' to a desired state and less costly refresh.
 */
const selection = reactive<CheckoutSelection>({
  plan: null,
  // Use the business's defined currency if available. Fallback to 'CHF' for Switzerland. Otherwise, use 'EUR'.
  currency: businessStore.billing.currency ?? (platform.value === 'CH' ? 'CHF' : 'EUR'),
  billingCycle: businessStore.isTrialing || businessStore.currentPricePlan?.cycle !== 'MONTHLY' ? 'yearly' : 'monthly',
})

let previewAbortController: AbortController | null = null

const loadPaymentDetails = async () => {
  if (businessStore.isStarterOrCbTrial) {
    paymentMethod.value = await getCurrentPaymentMethod()

    if (paymentMethod.value?.billing) {
      await businessStore.getBillingAddress()
      const { address1, address2, city, country, postalCode, vat } = businessStore.billing
      Object.assign(addressData, { address1, address2, city, country, postalCode, vat })
    }
  }
}

watch(
  () => route.fullPath,
  async () => {
    const planName = route.query.plan as Lowercase<PricePlanName> | undefined
    if (route.query.currency && platform.value === 'CH') {
      /* currency cannot change on EU platform */
      selection.currency = route.query.currency as Currency
    }
    if (route.query.cycle) {
      selection.billingCycle = route.query.cycle as BillingCycle
    }
    if (planName) {
      viewInFocus.value = 'upgrade'
      await updatePricePlans()
      selection.plan = getPlan(planName)
      if (isPlanUpgrade.value && !paymentData.paymentPreview) {
        await generatePaymentPreview(minimumSeats.value)
      }
      if (!isPlanUpgrade.value && !paymentData.paymentIntent) {
        isFetchingPrice.value = true
        await generatePaymentIntent(addressData)
        isFetchingPrice.value = false
      }
    } else {
      viewInFocus.value = 'plans'
      selection.plan = null
      previewAbortController?.abort()
      abortPaymentIntent()
      paymentData.paymentPreview = null
      paymentData.paymentIntent = null
    }
  },
  { immediate: true }
)

const updateSelection = async (newSelection: {
  plan?: PricePlan | null
  currency?: Currency
  cycle?: BillingCycle
}) => {
  const { plan, currency, cycle } = newSelection

  if (plan) selection.plan = plan
  /* currency cannot change on EU platform */
  if (currency && platform.value === 'CH') selection.currency = currency
  if (cycle) selection.billingCycle = cycle

  return navigateTo({
    replace: true,
    path: route.path,
    query: { plan: plan?.name.toLowerCase(), currency, cycle },
  })
}

const generatePaymentPreview = async (newSeats: number) => {
  previewAbortController?.abort()
  previewAbortController = new AbortController()

  isFetchingPrice.value = true
  try {
    const response = await paymentRepository.previewUpgradeSubscriptionPrice(
      businessStore.id,
      selection.plan!.id,
      selection.billingCycle,
      newSeats,
      selection.currency,
      selectedCouponCode.value,
      previewAbortController.signal
    )
    paymentData.paymentPreview = response
    isFetchingPrice.value = false
  } catch (error) {
    if (!isAbortError(error)) {
      showError()
      paymentData.paymentIntent = null
      isFetchingPrice.value = false
    }
  }
}

const update = async (newSeats: number | undefined, cycle: BillingCycle | undefined, coupon: string | undefined) => {
  seats.value = Math.max(newSeats ?? minimumSeats.value, minimumSeats.value)
  selection.billingCycle = cycle ?? 'yearly'
  selectedCouponCode.value = coupon ?? ''
  updateSelection({ plan: selection.plan, currency: selection.currency, cycle: selection.billingCycle })
  if (isPlanUpgrade.value) {
    await generatePaymentPreview(seats.value)
  } else {
    isFetchingPrice.value = true
    await generatePaymentIntent(addressData)
    isFetchingPrice.value = false
  }
}

const addressData = reactive<Address>({
  city: '',
  country: null,
  address1: '',
  address2: '',
  postalCode: '',
  vat: '',
})

watch(
  () => addressData.country,
  async country => {
    /* Currency cannot change on EU platform and is fixed when updating a currently running subscription from CB */
    if (platform.value === 'CH' && !businessStore.isStarterOrCbTrial) {
      if (country !== 'CH' && selection.currency === 'CHF') {
        selection.currency = 'EUR'
      }
      if (country === 'CH' && selection.currency !== 'CHF') {
        selection.currency = 'CHF'
      }
    }
  }
)

const isBillingDataValid = ref(false) // Form data validity

const trackFirstPaidSubscription = () => {
  const amounts = paymentData.paymentIntent ?? paymentData.paymentPreview
  const netAmount = amounts ? amounts.amountSubtotal - amounts.amountDiscountTotal : Number.NaN

  if (!businessStore.id || Number.isNaN(netAmount)) {
    logError(
      'ADCELL conversion not tracked: missing business id or payment amounts',
      new Error(`businessId=${businessStore.id}, netAmount=${netAmount}`)
    )
    return
  }

  $adcell.trackPaidSubscription({
    reference: buildAdcellPurchaseReference(businessStore.id, new Date()),
    netAmount,
    vouchers: selectedCouponCode.value ? [selectedCouponCode.value] : [],
  })
}

const completeUpgrade = async (wasFirstPaidSubscription: boolean) => {
  await businessStore.getBusiness()

  if (wasFirstPaidSubscription) trackFirstPaidSubscription()

  await navigateTo({ name: 'business-billing-plan' })
  widgetStore.createSnackbar({
    message: t('business.upgrade.message.upgrade_success'),
  })
}

const onPaymentSuccess = async (e: PaymentComponentSuccessEvent) => {
  if (!selection.plan) throw new PaymentError('No selected plan detected', 'billing.settings.cc.error.setup')

  isLoading.value = true
  const coupon = selectedCouponCode.value
  const wasFirstPaidSubscription = isFirstPaidSubscription.value

  const subscriptionPayload = {
    type: selection.plan.id,
    cycle: selection.billingCycle,
    paymentIntentId: e.paymentIntentId,
    seats: seats.value,
    ...(coupon && { coupon }),
  }

  if (blockedBusiness.value || businessStore.isTrialing || businessStore.isStarterPlan) {
    await businessStore.updateBillingAddress(addressData)
    await paymentStore.upgradeSubscription(subscriptionPayload)
  } else {
    await paymentStore.directUpgradeSubscription(
      subscriptionPayload,
      addressData,
      selection.currency,
      businessName.value
    )
  }

  await completeUpgrade(wasFirstPaidSubscription)
  isLoading.value = false
}

const minimumSeats = computed(() => {
  if (selection.plan?.id.startsWith('INDIVIDUAL')) return 1
  if (businessStore.isLocked) return businessStore.members.length
  const currentSeats = businessStore.pricePlan?.current?.base?.seats?.total || 1
  const membersAndInvitees = businessStore.members.length + businessStore.invitedMembers.length
  return Math.max(currentSeats, membersAndInvitees)
})

// prevents seats from going below minimum
watch(minimumSeats, min => {
  if (seats.value < min) seats.value = min
})

// resets seats when plan changes (handles INDIVIDUAL plan switch, always 1 seat, others will be moved to free users)
watch(
  () => selection.plan,
  () => {
    seats.value = minimumSeats.value
  }
)

const upgradeSubscription = async (coupon?: string) => {
  if (!selection.plan) throw new PaymentError('No selected plan detected', 'billing.settings.cc.error.setup')

  const subscriptionPayload = {
    type: selection.plan.id,
    cycle: selection.billingCycle,
    seats: seats.value,
    ...(coupon && { coupon }),
  }

  await paymentStore.upgradeSubscription(subscriptionPayload)
}

const isUpgradeAllowed = computed(() => {
  if (isPlanUpgrade.value) return true
  return isBillingDataValid.value
})

const abortUpgrade = () => {
  useRouter().back()
}

const isWaitingForUpgrade = ref(false)

watch(
  () => widgetStore.activeDialog,
  (newDialog, oldDialog) => {
    if (oldDialog?.name === 'DialogAddPaymentMethod' && !newDialog && !isWaitingForUpgrade.value) {
      isLoading.value = false
    }
    isWaitingForUpgrade.value = false
  }
)

const upgrade = async (newSeats?: number, coupon?: string) => {
  seats.value = Math.max(newSeats ?? minimumSeats.value, minimumSeats.value)
  isLoading.value = true
  if (!isPlanUpgrade.value) {
    await createPlan()
  } else {
    await upgradePlan(coupon)
  }
}

const createPlan = async () => {
  if (businessStore.isStarterOrCbTrial && hasPaymentCardOrBankAccount.value) {
    const wasFirstPaidSubscription = isFirstPaidSubscription.value

    try {
      await businessStore.updateBillingAddress(addressData)
      await upgradeSubscription(selectedCouponCode.value || undefined)
      await completeUpgrade(wasFirstPaidSubscription)
    } catch {
      showError()
    } finally {
      isLoading.value = false
    }
    return
  }

  if (isFreeUser.value) {
    await generatePaymentIntent(addressData)
  } else {
    await businessStore.updateBillingAddress(addressData)
    await generatePaymentIntent()
  }

  widgetStore.setDialog(
    'DialogAddPaymentMethod',
    {
      intent: paymentData.paymentIntent ?? undefined,
      address: addressData,
      selection: selection,
      seats: seats.value,
      coupon: selectedCouponCode.value,
    },
    async ({ intentId }) => {
      isWaitingForUpgrade.value = true
      await onPaymentSuccess({ paymentIntentId: intentId })
    }
  )
}

const upgradePlan = async (coupon?: string) => {
  try {
    await upgradeSubscription(coupon)
    await navigateTo({ name: 'business-billing-plan' })
    widgetStore.createSnackbar({
      message: t('business.upgrade.message.change_success'),
    })
    isLoading.value = false
  } catch {
    showError()
  }
}

onBeforeMount(() => {
  void updatePricePlans()
  if (businessStore.id) {
    void businessStore.getBusiness()
  }
})

onMounted(() => {
  loadPaymentDetails()
})

onBeforeUnmount(() => {
  previewAbortController?.abort()
  abortPaymentIntent()
})

// FIXME: currency-switcher on COM platform, has incorrect order
</script>

<template>
  <v-main>
    <toolbar-base :title="toolbarTitle" is-closeable @close="abortUpgrade" />
    <!-- Plans -->
    <div v-if="viewInFocus === 'plans'" class="plans-view">
      <div class="content">
        <h1 class="text-h3 text-grey-darker text-center font-weight-bold my-12">
          {{ proPlanActive ? $t('business.upgrade.plans.scale_title') : $t('business.upgrade.plans.title') }}
        </h1>
        <plan-selection
          :show-currency-switcher="platform === 'CH' && !isChargebeeTrial"
          :plans="pricePlans"
          :currency="selection.currency"
          :cycle="selection.billingCycle"
          :is-plan-upgrade="isPlanUpgrade"
          @choose="updateSelection"
        />
      </div>
      <div class="compare">
        <text-button color="primary" :href="$t('business.upgrade.plans.compare_all.link')" target="_blank">
          <span class="font-weight-bold">{{ $t('business.upgrade.plans.compare_all.text') }}</span>
          <v-icon color="primary" end>{{ icons.arrowRight }}</v-icon>
        </text-button>
      </div>
    </div>
    <!-- Payment -->
    <div v-else-if="viewInFocus === 'upgrade'" class="payment-view">
      <div v-if="!isPlanUpgrade || trialConvert" class="payment-view__info">
        <div class="content">
          <text-button
            class="mb-8 justify-start"
            color="primary"
            block
            @click="updateSelection({ plan: null, currency: selection.currency, cycle: selection.billingCycle })"
          >
            <v-icon color="primary" start>{{ icons.arrowLeft }}</v-icon>
            {{ $t('business.upgrade.plans.choose_other') }}
          </text-button>
          <div class="text-h5 text-grey-darker font-weight-bold mb-6">
            {{ $t('business.upgrade.plans.add_payment_details') }}
          </div>
          <div class="text-h6 text-grey-darker font-weight-bold mb-6">{{ $t('global.billing_address') }}</div>
          <billing-form v-model="addressData" @change="isValid => (isBillingDataValid = isValid)" />
        </div>
      </div>
      <div class="payment-view__overview">
        <div class="content">
          <text-button
            v-if="isPlanUpgrade && !trialConvert"
            class="mb-8 justify-start"
            color="primary"
            block
            @click="updateSelection({ plan: null, currency: selection.currency, cycle: selection.billingCycle })"
          >
            <v-icon color="primary" start>{{ icons.arrowLeft }}</v-icon>
            {{ $t('business.upgrade.plans.choose_other') }}
          </text-button>
          <plan-upgrade-card
            v-if="selection.plan"
            :billing-cycle="selection.billingCycle"
            :currency="selection.currency"
            :price-plan="selection.plan"
            :preview="paymentData.paymentPreview"
            :min-seats="minimumSeats"
            :is-disabled="!isUpgradeAllowed"
            :is-loading="isLoading"
            :is-fetching-price="isFetchingPrice"
            :has-payment-method="hasPaymentCardOrBankAccount"
            @change-cycle="cycle => updateSelection({ cycle, currency: selection.currency, plan: selection.plan })"
            @update="update"
            @upgrade="upgrade"
          />
        </div>
      </div>
    </div>
  </v-main>
</template>

<style lang="scss" scoped>
.error-alert {
  position: fixed;
  top: 0;
  z-index: 999;
  left: 50%;
  transform: translateX(-50%);
}

.plans-view {
  $compare-features-height: 110px;

  padding: 0 24px $compare-features-height + 24px;
  max-width: 1304px; // This is the exact width of 4 plan cards, plus gaps
  width: fit-content;
  margin-left: auto;
  margin-right: auto;

  .compare {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    display: grid;
    place-items: center;
    background-color: $c-grey-fine;
    height: $compare-features-height;
    padding: $bw * 4;
  }
}

.payment-view {
  max-width: $site-width;
  margin: 0 auto;
  display: flex;
  justify-content: center;

  &__info {
    width: 50%;
    overflow-y: auto;
    max-height: 100%;
    padding: 8 * $bw;
  }

  &__overview {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: stretch;
    width: 50%;
    padding: 8 * $bw;
  }

  .content {
    max-width: 700px;
    margin: 0 auto;
  }
}
</style>
